import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Modal, Icon, Pagination } from "../";
import { useBusinesses } from "@finitless/shared/hooks";
import { useApiStore, useUserStore } from "@finitless/shared/stores";
import { useTranslation } from "react-i18next";
import { useMenuImportRun } from "@/hooks/importers/useMenuImportRun";
import { clearRun, isRunResolved } from "@/lib/menuImportRuns";

const focusStatus = (node) => {
  if (node) node.focus();
};

const formatStartedAt = (value) =>
  new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));

/**
 * Generic menu import modal — URL input + business multi-select.
 * Same layout for all platforms (DoorDash, Uber Eats, etc.).
 * Platform-specific: `validateForm`, import hook, and `buildImportParams(form, …)` come from config.
 */
const MenuImportModal = ({ isOpen, onClose, config = {} }) => {
  const {
    titleKey,
    subtitleKey,
    urlLabelKey,
    urlPlaceholderKey,
    urlHelperKey,
    trackerLabelKey,
    trackerPlaceholderKey,
    trackerHelperKey,
    orderingProjectLabelKey,
    orderingProjectPlaceholderKey,
    orderingBusinessSegmentLabelKey,
    orderingBusinessSegmentPlaceholderKey,
    orderingStructuredHintKey,
    statusImportingKey,
    includeAllMenuVariantsKey,
    validateForm,
    useImport,
    buildImportParams,
    source,
    sourceNameKey,
  } = config;
  const { t } = useTranslation("menuImporters");
  const navigate = useNavigate();
  const {
    triggerImport,
    isImporting,
    status,
    error: importError,
    result,
    startedAt,
    reset,
  } = useImport();
  const project = useApiStore((state) => state.project);
  const storedRun = useMenuImportRun(source);
  const runRef = { project, source };
  const resumedStatus =
    status === "idle" &&
    ["importing", "unknown"].includes(storedRun?.status) &&
    !isRunResolved(storedRun)
      ? storedRun.status
      : null;
  const viewStatus = resumedStatus || status;
  const orderingApiBaseUrl = useApiStore((state) => state.getBaseUrl());
  const user = useUserStore((state) => state.user);

  const [urlInput, setUrlInput] = useState("");
  const [trackerInput, setTrackerInput] = useState("");
  const [orderingPlusProjectId, setOrderingPlusProjectId] = useState("");
  const [orderingBusinessSegment, setOrderingBusinessSegment] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedBusinesses, setSelectedBusinesses] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [includeAllMenuVariants, setIncludeAllMenuVariants] = useState(true);
  const [editing, setEditing] = useState(false);
  const [lastParams, setLastParams] = useState(null);

  const pageSize = 10;

  useEffect(() => {
    const timer = setTimeout(() => setSearchTerm(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const { data, isLoading, error, refetch } = useBusinesses({
    enabledFilter: "all",
    searchTerm,
    page: currentPage,
    pageSize,
  });

  const businesses = data?.businesses || [];
  const pagination = data?.pagination || {};
  const importForm = useMemo(
    () => ({
      url: urlInput,
      tracker: trackerInput,
      orderingPlusProjectId,
      orderingBusinessSegment,
    }),
    [urlInput, trackerInput, orderingPlusProjectId, orderingBusinessSegment],
  );
  const formValidation = useMemo(
    () => validateForm(importForm, { projectApiRoot: orderingApiBaseUrl }),
    [importForm, orderingApiBaseUrl, validateForm],
  );
  const showFormError =
    Boolean(formValidation.error) &&
    (urlInput.trim() !== "" ||
      trackerInput.trim() !== "" ||
      orderingPlusProjectId.trim() !== "" ||
      orderingBusinessSegment.trim() !== "");
  const isImportEnabled = formValidation.valid && selectedBusinesses.length > 0;
  const hasOrderingStructuredFields = Boolean(orderingProjectLabelKey);
  const hasTrackerField = Boolean(trackerLabelKey);

  useEffect(() => {
    if (!isOpen) {
      setUrlInput("");
      setTrackerInput("");
      setOrderingPlusProjectId("");
      setOrderingBusinessSegment("");
      setSearchInput("");
      setSearchTerm("");
      setSelectedBusinesses([]);
      setCurrentPage(1);
      setIncludeAllMenuVariants(true);
      setEditing(false);
      setLastParams(null);
    }
  }, [isOpen]);

  const handleImport = () => {
    if (!isImportEnabled || isImporting) return;
    const businessIds = selectedBusinesses.map((b) => b.id);
    const params = {
      ...buildImportParams(
        importForm,
        businessIds,
        project,
        orderingApiBaseUrl,
        includeAllMenuVariants,
        user?.email,
      ),
      businessNames: selectedBusinesses
        .map((business) => business.name)
        .filter(Boolean),
    };
    setLastParams(params);
    setEditing(false);
    triggerImport(params);
  };

  const handleRetry = () => {
    setEditing(false);
    if (lastParams) {
      triggerImport(lastParams);
      return;
    }
    clearRun(runRef);
  };

  const handleClose = () => {
    setEditing(false);
    setLastParams(null);
    reset();
    onClose?.();
  };

  const handleGoToProducts = () => {
    if (selectedBusinesses.length === 1 && selectedBusinesses[0].slug) {
      navigate(`/menu/products/${selectedBusinesses[0].slug}`);
    } else {
      navigate("/menu/products");
    }
    handleClose();
  };

  const showImporting = viewStatus === "importing";
  // Importing renders as an absolute overlay on top of the form; the form
  // stays mounted underneath so the overlay keeps the modal body height.
  const showOutcome =
    !editing &&
    (viewStatus === "success" ||
      viewStatus === "failed" ||
      viewStatus === "unknown");
  const shownStartedAt = startedAt || storedRun?.startedAt;

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={isImporting ? undefined : handleClose}
      title={t(titleKey)}
      subtitle={t(subtitleKey)}
      maxWidth="max-w-2xl"
      className="sm:max-h-[80vh]"
    >
      <div className="relative space-y-6">
        {showImporting && (
          <div
            className="bg-background/80 absolute inset-0 z-10 flex flex-col items-center justify-center rounded-lg backdrop-blur-sm"
            aria-live="polite"
          >
            <div className="border-primary h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
            <p className="text-foreground mt-3 text-sm">
              {t(statusImportingKey)}
            </p>
            {shownStartedAt && (
              <p className="text-foreground mt-1 text-sm">
                {formatStartedAt(shownStartedAt)}
              </p>
            )}
          </div>
        )}

        {showOutcome ? (
          <div
            ref={focusStatus}
            tabIndex={-1}
            role={viewStatus === "failed" ? "alert" : undefined}
            className="border-border bg-card space-y-4 rounded-lg border p-4"
          >
            {viewStatus === "success" && (
              <>
                <p className="text-foreground text-sm">{t("status.success")}</p>
                <p className="text-foreground text-sm">
                  {t("status.appliedCount", {
                    count: result?.appliedCount ?? 0,
                  })}
                </p>
              </>
            )}
            {viewStatus === "failed" && (
              <>
                <p className="text-error text-sm font-semibold">
                  {t("status.failedTitle", {
                    source: t(sourceNameKey || titleKey),
                  })}
                </p>
                <p className="text-error text-sm">{importError?.message}</p>
              </>
            )}
            {viewStatus === "unknown" && (
              <>
                <p className="text-foreground text-sm">
                  {t("status.unknownTitle")}
                </p>
                <p className="text-foreground text-sm">
                  {t("status.unknownBody")}
                </p>
              </>
            )}
            <div className="flex justify-end gap-3">
              {viewStatus === "success" && (
                <>
                  <Button
                    type="button"
                    onClick={handleGoToProducts}
                    emphasis="solid"
                    intent="brand"
                    size="touch"
                  >
                    {selectedBusinesses.length === 1 &&
                    selectedBusinesses[0].slug
                      ? t("actions.goToStoreProducts", {
                          storeName:
                            selectedBusinesses[0].name ||
                            t("actions.thisStore"),
                        })
                      : t("actions.goToProducts")}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      setEditing(false);
                      reset();
                      clearRun(runRef);
                    }}
                    emphasis="outline"
                    intent="neutral"
                    size="touch"
                  >
                    {t("actions.importAnother")}
                  </Button>
                </>
              )}
              {viewStatus === "failed" && importError?.retryable && (
                <Button
                  type="button"
                  onClick={handleRetry}
                  emphasis="solid"
                  intent="brand"
                  size="touch"
                >
                  {t("actions.retry")}
                </Button>
              )}
              {viewStatus === "failed" && (
                <Button
                  type="button"
                  onClick={() => {
                    setEditing(true);
                    clearRun(runRef);
                  }}
                  emphasis="outline"
                  intent="neutral"
                  size="touch"
                >
                  {t("actions.editUrl")}
                </Button>
              )}
              {viewStatus === "unknown" && (
                <>
                  <Button
                    type="button"
                    onClick={handleGoToProducts}
                    emphasis="solid"
                    intent="brand"
                    size="touch"
                  >
                    {t("actions.checkProducts")}
                  </Button>
                  <Button
                    type="button"
                    onClick={handleRetry}
                    emphasis="outline"
                    intent="neutral"
                    size="touch"
                  >
                    {t("actions.retry")}
                  </Button>
                </>
              )}
            </div>
          </div>
        ) : (
          <>
            <div>
              <label
                htmlFor="menu-import-url-input"
                className="text-foreground mb-1.5 block text-sm font-medium"
              >
                {t(urlLabelKey)}
              </label>
              <input
                id="menu-import-url-input"
                type="text"
                inputMode="url"
                autoComplete="url"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder={t(urlPlaceholderKey)}
                aria-describedby={
                  showFormError ? "url-helper form-import-error" : "url-helper"
                }
                aria-invalid={showFormError && urlInput.trim() !== ""}
                className="border-border bg-background text-foreground placeholder:text-muted-foreground focus:ring-ring h-10 w-full rounded-lg border px-4 py-2 focus:border-transparent focus:ring-2 focus:outline-none"
              />
              <p id="url-helper" className="text-muted-foreground mt-1 text-xs">
                {t(urlHelperKey)}
              </p>
            </div>

            {hasTrackerField && (
              <div>
                <label
                  htmlFor="menu-import-tracker-input"
                  className="text-foreground mb-1.5 block text-sm font-medium"
                >
                  {t(trackerLabelKey)}
                </label>
                <input
                  id="menu-import-tracker-input"
                  type="text"
                  autoComplete="off"
                  value={trackerInput}
                  onChange={(e) => setTrackerInput(e.target.value)}
                  placeholder={t(trackerPlaceholderKey)}
                  aria-describedby="tracker-helper"
                  className="border-border bg-background text-foreground placeholder:text-muted-foreground focus:ring-ring h-10 w-full rounded-lg border px-4 py-2 font-mono text-sm focus:border-transparent focus:ring-2 focus:outline-none"
                />
                <p
                  id="tracker-helper"
                  className="text-muted-foreground mt-1 text-xs"
                >
                  {t(trackerHelperKey)}
                </p>
              </div>
            )}

            {hasOrderingStructuredFields && (
              <div className="space-y-4">
                <p className="text-muted-foreground text-xs">
                  {t("modal.orderingOr")}
                </p>
                <div>
                  <label
                    htmlFor="menu-import-ordering-project"
                    className="text-foreground mb-1.5 block text-sm font-medium"
                  >
                    {t(orderingProjectLabelKey)}
                  </label>
                  <input
                    id="menu-import-ordering-project"
                    type="text"
                    autoComplete="off"
                    value={orderingPlusProjectId}
                    onChange={(e) => setOrderingPlusProjectId(e.target.value)}
                    placeholder={t(orderingProjectPlaceholderKey)}
                    className="border-border bg-background text-foreground placeholder:text-muted-foreground focus:ring-ring h-10 w-full rounded-lg border px-4 py-2 focus:border-transparent focus:ring-2 focus:outline-none"
                  />
                </div>
                <div>
                  <label
                    htmlFor="menu-import-ordering-segment"
                    className="text-foreground mb-1.5 block text-sm font-medium"
                  >
                    {t(orderingBusinessSegmentLabelKey)}
                  </label>
                  <input
                    id="menu-import-ordering-segment"
                    type="text"
                    autoComplete="off"
                    value={orderingBusinessSegment}
                    onChange={(e) => setOrderingBusinessSegment(e.target.value)}
                    placeholder={t(orderingBusinessSegmentPlaceholderKey)}
                    className="border-border bg-background text-foreground placeholder:text-muted-foreground focus:ring-ring h-10 w-full rounded-lg border px-4 py-2 focus:border-transparent focus:ring-2 focus:outline-none"
                  />
                </div>
                <p className="text-muted-foreground text-xs">
                  {t(orderingStructuredHintKey)}
                </p>
              </div>
            )}

            {showFormError && (
              <p
                id="form-import-error"
                className="text-error text-xs"
                role="alert"
              >
                {t(formValidation.error)}
              </p>
            )}

            {includeAllMenuVariantsKey && (
              <div className="flex items-center gap-3">
                <input
                  id="menu-import-include-variants"
                  type="checkbox"
                  checked={includeAllMenuVariants}
                  onChange={(e) => setIncludeAllMenuVariants(e.target.checked)}
                  className="border-border bg-background text-primary focus:ring-ring h-4 w-4 rounded focus:ring-2 focus:ring-offset-0"
                />
                <label
                  htmlFor="menu-import-include-variants"
                  className="text-foreground cursor-pointer text-sm"
                >
                  {t(includeAllMenuVariantsKey)}
                </label>
              </div>
            )}

            <div>
              <div className="mb-3 flex items-center justify-between">
                <label className="text-foreground block text-sm font-medium">
                  {t("modal.businessesLabel")}
                </label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    onClick={() =>
                      setSelectedBusinesses((prev) => {
                        const byId = new Map(prev.map((b) => [b.id, b]));
                        businesses.forEach((b) =>
                          byId.set(b.id, {
                            id: b.id,
                            slug: b.slug,
                            name: b.name,
                          }),
                        );
                        return Array.from(byId.values());
                      })
                    }
                    variant="ghost"
                    size="sm"
                    className="hover:bg-accent text-[var(--od-action-content)]"
                  >
                    {t("modal.selectAll")}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setSelectedBusinesses([])}
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground hover:bg-accent hover:text-foreground"
                  >
                    {t("modal.clear")}
                  </Button>
                </div>
              </div>

              <div className="relative mb-3">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <Icon
                    name="Search"
                    className="text-muted-foreground h-5 w-5"
                  />
                </div>
                <input
                  type="text"
                  placeholder={t("modal.businessSearchPlaceholder")}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="border-border bg-background text-foreground placeholder:text-muted-foreground focus:ring-ring h-10 w-full rounded-lg border py-2 pr-4 pl-10 focus:border-transparent focus:ring-2 focus:outline-none"
                />
              </div>

              {isLoading ? (
                <div className="grid grid-cols-1 gap-3">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div
                      key={i}
                      className="border-border bg-card animate-pulse rounded-lg border p-4"
                    >
                      <div className="flex items-center gap-3">
                        <div className="border-border bg-muted h-5 w-5 rounded border" />
                        <div className="bg-muted h-10 w-10 rounded-lg" />
                        <div className="flex-1">
                          <div className="bg-muted mb-1 h-4 w-32 rounded" />
                          <div className="bg-muted h-3 w-20 rounded" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : error ? (
                <div className="border-error rounded-lg border bg-[var(--error-bg)] p-4">
                  <p className="text-error text-sm">
                    {error.message || t("status.error")}
                  </p>
                  <Button
                    type="button"
                    emphasis="solid"
                    intent="danger"
                    size="sm"
                    onClick={() => refetch()}
                    className="mt-2"
                  >
                    {t("actions.retry")}
                  </Button>
                </div>
              ) : businesses.length === 0 ? (
                <div className="border-border bg-card rounded-lg border p-6 text-center">
                  <p className="text-muted-foreground text-sm">
                    {t("empty.noStores")}
                  </p>
                </div>
              ) : (
                <>
                  <div className="border-border bg-card max-h-64 space-y-1 overflow-y-auto rounded-lg border p-2">
                    {businesses.map((business) => {
                      const isSelected = selectedBusinesses.some(
                        (b) => b.id === business.id,
                      );
                      return (
                        <Button
                          key={business.id}
                          type="button"
                          onClick={() =>
                            setSelectedBusinesses((prev) =>
                              prev.some((b) => b.id === business.id)
                                ? prev.filter((b) => b.id !== business.id)
                                : [
                                    ...prev,
                                    {
                                      id: business.id,
                                      slug: business.slug,
                                      name: business.name,
                                    },
                                  ],
                            )
                          }
                          variant="ghost"
                          className={`flex h-auto w-full cursor-pointer items-center justify-start gap-3 rounded-lg p-3 text-left whitespace-normal transition-colors ${
                            isSelected
                              ? "bg-accent hover:bg-accent"
                              : "hover:bg-muted"
                          }`}
                        >
                          <div
                            className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border transition-colors ${
                              isSelected
                                ? "border-primary bg-primary"
                                : "border-border bg-background"
                            }`}
                          >
                            {isSelected && (
                              <Icon
                                name="Check"
                                className="h-3 w-3 text-white"
                              />
                            )}
                          </div>
                          {business.logo ? (
                            <img
                              src={business.logo}
                              alt=""
                              className="h-10 w-10 flex-shrink-0 rounded-lg object-cover"
                            />
                          ) : (
                            <div className="border-border bg-muted flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border">
                              <Icon
                                name="Store"
                                className="text-muted-foreground h-5 w-5"
                              />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="text-foreground truncate text-sm font-medium">
                              {business.name}
                            </p>
                            {(business.city?.name ||
                              (typeof business.city === "string" &&
                                business.city)) && (
                              <p className="text-muted-foreground truncate text-xs">
                                {business.city?.name || business.city}
                              </p>
                            )}
                          </div>
                        </Button>
                      );
                    })}
                  </div>

                  {pagination?.total_pages > 1 && (
                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-muted-foreground text-sm">
                        {t("pagination.showing", {
                          from: pagination.from,
                          to: pagination.to,
                          total: pagination.total,
                        })}
                      </span>
                      <Pagination
                        currentPage={currentPage}
                        totalPages={pagination.total_pages}
                        onPageChange={setCurrentPage}
                        maxVisiblePages={5}
                      />
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="border-border flex justify-end gap-3 border-t pt-4">
              <Button
                type="button"
                onClick={handleClose}
                disabled={isImporting}
                emphasis="outline"
                intent="neutral"
                size="touch"
              >
                {t("modal.cancelButton")}
              </Button>
              <Button
                type="button"
                onClick={handleImport}
                disabled={!isImportEnabled || isImporting}
                emphasis="solid"
                intent="brand"
                size="touch"
              >
                {t("modal.importButton")}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default MenuImportModal;
